import { Box, Typography } from '@mui/material';
import React from 'react';

import { PASSWORD_SPECIAL_CHARACTER_REGEX } from '../../utils/passwordFormHelpers';
import { TextFieldComponent } from '../text-field';
import { colors } from '../theme/theme';

const STRENGTH_CHECKS = [
    (p: string) => p.length >= 8 && p.length <= 20,
    (p: string) => /[A-Z]/.test(p),
    (p: string) => /[a-z]/.test(p),
    (p: string) => /\d/.test(p),
    (p: string) => PASSWORD_SPECIAL_CHARACTER_REGEX.test(p),
];

export const getPasswordHintMessage = (password: string): string => {
    const missing: string[] = [];
    if (password.length < 8) missing.push('at least 8 characters');
    else if (password.length > 20) missing.push('20 characters or fewer');
    if (!/[A-Z]/.test(password)) missing.push('an uppercase letter');
    if (!/[a-z]/.test(password)) missing.push('a lowercase letter');
    if (!/\d/.test(password)) missing.push('a number');
    if (!PASSWORD_SPECIAL_CHARACTER_REGEX.test(password)) missing.push('a special character');
    if (missing.length === 0) return '';
    if (missing.length === 1) return `Your password needs ${missing[0]}.`;
    const last = missing.pop();
    return `Your password needs ${missing.join(', ')} and ${last}.`;
};

export const getStrengthLevel = (password: string): 0 | 1 | 2 | 3 => {
    if (!password) return 0;
    const score = STRENGTH_CHECKS.filter(check => check(password)).length;
    if (score <= 1) return 1;
    if (score <= 4) return 2;
    return 3;
};

export const LEVEL_CONFIG = {
    0: { label: '', color: colors.greyPreset[20] },
    1: { label: 'Too weak', color: colors.errorPreset[10] },
    2: { label: 'Medium', color: colors.warningPreset[50] },
    3: { label: 'Strong', color: colors.successPreset[30] },
} as const;

interface LoginPasswordStrengthFieldProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: boolean;
    errorValue?: string;
    onForgotPassword?: () => void;
    maxLength?: number;
    autoComplete?: string;
}

const LoginPasswordStrengthField: React.FC<LoginPasswordStrengthFieldProps> = ({
    value,
    onChange,
    error,
    errorValue,
    onForgotPassword,
    maxLength,
    autoComplete,
}) => {
    const level = getStrengthLevel(value);
    const { label: strengthLabel, color: strengthColor } = LEVEL_CONFIG[level];
    const hasContent = value.length > 0;

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>
                    Password
                </Typography>
                {hasContent && (
                    <Typography
                        variant="caption"
                        sx={{ color: strengthColor, fontWeight: 600 }}
                        role="status"
                        aria-live="polite"
                    >
                        {strengthLabel}
                    </Typography>
                )}
            </Box>

            <TextFieldComponent
                type="password"
                value={value}
                onChange={onChange}
                variant="outlined"
                error={!!error}
                maxLength={maxLength}
                autoComplete={autoComplete}
                ariaLabel="Password"
                sx={
                    hasContent && !error
                        ? {
                              '& .MuiOutlinedInput-root': {
                                  '& fieldset': { borderColor: `${strengthColor} !important` },
                                  '&:hover fieldset': { borderColor: `${strengthColor} !important` },
                                  '&.Mui-focused fieldset': { borderColor: `${strengthColor} !important` },
                              },
                          }
                        : {}
                }
            />

            {hasContent && (
                <Box display="flex" gap={0.75} mt={0.75}>
                    {[1, 2, 3].map(segment => (
                        <Box
                            key={segment}
                            sx={{
                                flex: 1,
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: segment <= level ? strengthColor : colors.greyPreset[20],
                                transition: 'background-color 0.2s ease',
                            }}
                        />
                    ))}
                </Box>
            )}

            {hasContent && getPasswordHintMessage(value) && (
                <Typography
                    variant="caption"
                    sx={{ color: strengthColor, mt: 0.5, display: 'block' }}
                    role="status"
                    aria-live="polite"
                >
                    {getPasswordHintMessage(value)}
                </Typography>
            )}

            {errorValue && (
                <Typography
                    variant="subtitle2"
                    sx={{ color: colors.errorPreset[10], mt: 0.5 }}
                    role="alert"
                    aria-live="assertive"
                    aria-atomic="true"
                >
                    {errorValue}
                </Typography>
            )}

            {onForgotPassword && (
                <Typography
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={onForgotPassword}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onForgotPassword();
                        }
                    }}
                    variant="subtitle2"
                    sx={{
                        color: colors.bluePreset[30],
                        mt: getPasswordHintMessage(value) ? 1.5 : hasContent ? 1 : 0.5,
                        cursor: 'pointer',
                        display: 'block',
                        width: 'fit-content',
                    }}
                >
                    Forgot Password?
                </Typography>
            )}
        </Box>
    );
};

export default LoginPasswordStrengthField;
